import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  Newspaper, Plus, Trash2, Loader2, X, Rocket, Zap, Users, Award, Handshake, Sparkles,
} from "lucide-react";
import { Card, EmptyState } from "../common/Primitives";
import api, { formatApiError } from "../../lib/api";

const KIND_META = {
  funding:     { label: "Funding",     Icon: Zap,       tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  product:     { label: "Product",     Icon: Rocket,    tone: "text-sky-700 bg-sky-50 border-sky-200" },
  hire:        { label: "Hire",        Icon: Users,     tone: "text-violet-700 bg-violet-50 border-violet-200" },
  milestone:   { label: "Milestone",   Icon: Award,     tone: "text-amber-700 bg-amber-50 border-amber-200" },
  partnership: { label: "Partnership", Icon: Handshake, tone: "text-teal-700 bg-teal-50 border-teal-200" },
  other:       { label: "Other",       Icon: Sparkles,  tone: "text-slate-700 bg-slate-50 border-slate-200" },
};

/**
 * FounderUpdateFeed
 * -----------------
 * Compact panel for founders to post short "Recent Development" cards that
 * flow into every investor's portfolio developments feed for this startup.
 * Rendered inline on the founder dashboard sidebar or Profile page.
 */
export default function FounderUpdateFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/startup/updates");
      setItems(r.data || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/startup/updates/${id}`);
      toast.success("Update deleted.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Card testId="section-founder-updates">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-1.5"><Newspaper size={12} /> Recent Developments</div>
          <div className="font-heading text-ink text-lg mt-1">
            Share news with your investors
            {items.length > 0 && (
              <span className="ml-2 text-sm text-ink-muted font-mono">({items.length})</span>
            )}
          </div>
          <div className="text-xs text-ink-muted mt-1">Every post lands in your investors&#39; portfolio dev feed instantly.</div>
        </div>
        <button
          onClick={() => setComposerOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-heading transition-colors"
          data-testid="founder-updates-add"
        >
          <Plus size={12} /> New Update
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-ink-muted py-4">Loading updates…</div>
      ) : items.length === 0 ? (
        <EmptyState
          title="No updates yet"
          hint="Post your first — funding, product launch, hire or a milestone."
        />
      ) : (
        <div className="space-y-2">
          {items.map((u) => {
            const meta = KIND_META[u.kind] || KIND_META.other;
            const Icon = meta.Icon;
            return (
              <div
                key={u.update_id}
                className="border border-white/10 rounded p-3 hover:border-emerald-300 transition-colors flex items-start gap-3"
                data-testid={`founder-update-${u.update_id}`}
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 border ${meta.tone}`}>
                  <Icon size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-heading uppercase tracking-widest border ${meta.tone}`}>
                      {meta.label}
                    </span>
                    <span className="text-[10px] font-mono text-ink-faint">
                      {(u.created_at || "").slice(0, 10)}
                    </span>
                  </div>
                  <div className="font-heading text-ink text-sm">{u.title}</div>
                  {u.body && (
                    <div className="text-xs text-ink-muted mt-1 line-clamp-2">{u.body}</div>
                  )}
                </div>
                <button
                  onClick={() => remove(u.update_id, u.title)}
                  className="text-rose-500 hover:text-rose-700 p-1.5 rounded transition-colors flex-shrink-0"
                  title="Delete update"
                  data-testid={`founder-update-delete-${u.update_id}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <ComposerModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onSaved={() => { setComposerOpen(false); load(); toast.success("Update posted to your investors."); }}
      />
    </Card>
  );
}

function ComposerModal({ open, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ kind: "milestone", title: "", body: "" });

  useEffect(() => { if (!open) setForm({ kind: "milestone", title: "", body: "" }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await api.post("/startup/updates", {
        kind: form.kind,
        title: form.title.trim(),
        body: form.body.trim() || null,
      });
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="founder-updates-modal">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-2xl border border-navy/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-navy/10 flex items-center justify-between">
          <div className="font-heading text-ink flex items-center gap-2">
            <Newspaper size={16} className="text-emerald-600" /> Post an update
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" data-testid="founder-updates-modal-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Category</div>
            <div className="flex flex-wrap gap-1.5" data-testid="founder-updates-kinds">
              {Object.entries(KIND_META).map(([k, meta]) => {
                const on = form.kind === k;
                const Icon = meta.Icon;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, kind: k }))}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] font-mono uppercase tracking-wider transition-colors ${
                      on ? meta.tone : "border-navy/15 text-ink-muted hover:border-emerald-300"
                    }`}
                    data-testid={`founder-updates-kind-${k}`}
                  >
                    <Icon size={11} /> {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Title</div>
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              maxLength={200}
              placeholder="e.g. Closed a ₹8 Cr strategic round from Northstar"
              className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm"
              data-testid="founder-updates-title"
              required
            />
          </div>

          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Details (optional)</div>
            <textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              rows={4}
              maxLength={2000}
              placeholder="Add context — what changed, what's next, key numbers…"
              className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm resize-none"
              data-testid="founder-updates-body"
            />
            <div className="text-[10px] font-mono text-ink-faint text-right mt-1">
              {form.body.length} / 2000
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-navy/15 text-ink text-sm hover:bg-navy/[0.04]" data-testid="founder-updates-cancel">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.title.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-heading transition-colors"
              data-testid="founder-updates-submit"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Post Update
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
